import React, {Fragment} from 'react'
import clsx from 'clsx'
import {useRouteLoaderData} from '@remix-run/react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {getTranslation} from '~/localization/translation'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import type {Product, RootLoaderResponse} from '~/types'
import {KeyFeaturesStarsIcon} from '~/icons/key-features-stars-icon'
import {getDomainConfig} from '~/domains'
import {cleanVariation, getPluralEnding} from '../badges/variants-indication'

interface KeyFeaturesEULayoutProps {
  placement: number
  productPageSlug?: string
  redirectUrl: string
  product: Product
  isKeyFeaturesOpen: boolean
  setIsKeyFeaturesOpen: (value: boolean) => void
  handleCardClick: (e: SyntheticEvent) => void
}
const KeyFeaturesEULayout: FunctionComponent<KeyFeaturesEULayoutProps> = ({
  placement,
  isKeyFeaturesOpen,
  setIsKeyFeaturesOpen,
  redirectUrl,
  handleCardClick,
  product,
}) => {
  const {domain, language, deviceType, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const {keyFeatures, variationDetails} = product

  const translations = getTranslation(language)
  const reportClick = useReportWidgetClick()
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const sneakPeakDefaultViewItems = deviceType === 'desktop' ? 2 : 1
  const variantsToDisplay = variationDetails
    ?.slice(0, deviceType !== 'desktop' ? 2 : 3)
    .filter((v) => v?.numberOfVariations > 1)

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'key-features-eu',
    widget_place: `card-${placement}`,
  })

  return (
    <div ref={widgetRef}>
      <div
        className={clsx(
          'overflow-hidden py-3 desktop:px-6 tablet:mx-6 tablet:py-2',
          isKeyFeaturesOpen ? '!max-h-[500px] tablet:!max-h-fit' : '!max-h-fit tablet:!max-h-fit',
        )}
      >
        <div className="mb-1 flex flex-row items-center gap-2">
          <KeyFeaturesStarsIcon />
          <span className="text-base font-semibold">{translations.ProductCard.keyFeatures}</span>
        </div>
        <ul className="list-outside px-1" data-testid="key-feature-content">
          {keyFeatures
            ?.slice(0, !isKeyFeaturesOpen ? sneakPeakDefaultViewItems : undefined)
            ?.map(({feature, description}, index) => (
              <li key={`${feature}-${index}`} className="mb-1 flex flex-row items-start">
                <p className="mb-auto text-sm font-medium">{index + 1}.</p>
                <p
                  className={clsx(
                    'ml-1 text-sm font-normal',
                    index === 0 && !isKeyFeaturesOpen && 'tablet:line-clamp-2',
                    index === 1 && !isKeyFeaturesOpen && 'desktop:line-clamp-1 desktop:w-[500px]',
                  )}
                >
                  <b className="font-semibold">{feature}</b>: {description}
                </p>
              </li>
            ))}
          {variantsToDisplay && variantsToDisplay?.length > 0 && isKeyFeaturesOpen && (
            <li className="mb-1 flex flex-row items-start tablet:flex-wrap">
              <p className="mb-auto text-sm font-medium">{(keyFeatures?.length ?? 0) + 1}.</p>
              <span className="ml-1 flex-shrink-0 text-sm font-semibold">
                {translations.ProductCard.variantsIndication.title}
              </span>
              &nbsp;
              <div className="flex flex-row items-start gap-1">
                {variantsToDisplay?.map((variation, i) => {
                  const isPlural = variation.numberOfVariations === 0 || variation.numberOfVariations > 1
                  const isLast = i === variantsToDisplay?.length - 1
                  return variation?.numberOfVariations > 1 ? (
                    <Fragment key={`${variation?.dimension}-${i}`}>
                      <p className="relative text-sm font-light capitalize text-blue-600">
                        {variation.numberOfVariations}&nbsp;
                        {getPluralEnding(cleanVariation(variation?.dimension), isPlural)}
                        <a
                          href={redirectUrl}
                          onClick={handleCardClick}
                          aria-label={`view ${product?.brand}`}
                          target="_blank"
                          rel="sponsored noreferrer"
                          className="absolute inset-0"
                        />
                      </p>
                      {!isLast && <span className="-mt-1 text-lg">•</span>}
                    </Fragment>
                  ) : null
                })}
              </div>
            </li>
          )}
        </ul>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setIsKeyFeaturesOpen(!isKeyFeaturesOpen)
          reportClick({
            widget_name: 'expand-key-features-button',
            widget_place: `card-${placement}`,
            trigger_type: isKeyFeaturesOpen ? 'close' : 'open',
          })
        }}
        className={clsx(
          'mb-2 ml-auto mr-6 flex h-8 flex-row items-center gap-2 stroke-[0.01em] text-sm font-medium capitalize',
          stylesBydomain.LISTPAGE_BUTTON_SECONDARY,
          'listpage_button_secondary',
        )}
      >
        {translations.ProductCard.viewMore(isKeyFeaturesOpen)}
        <IconChevronRightThick
          className={clsx(
            'size-4 transition-all duration-200 ease-in-out',
            isKeyFeaturesOpen ? '-rotate-90' : 'rotate-90',
            stylesBydomain.LISTPAGE_BUTTON_ICON_FILL,
            'listpage_button_icon_fill',
          )}
        />
      </button>
    </div>
  )
}

export default KeyFeaturesEULayout
